/**
 * paywall v1 — the 2024 wall. It asked for an email and then, and this
 * is the part everyone forgets, it did nothing. v3 replaced the ask
 * with a sign-up form and a nicer nothing.
 *
 * This file ships because QA still exercises the debug preview door.
 * Do NOT remove without moving the door. See debugUnlock below and
 * the test in wire-qa ("legacy preview door stays testable").
 */

export const V1_COPY = {
  wallTitle: "Readers like you keep this wire free",
  wallBody: "Enter your email to continue reading. We will not write, except to say this.",
  cta: "Continue reading",
};

export interface PreviewContent {
  title: string;
  kicker: string;
  body: string[];
  sealNote: string;
}

/**
 * QA preview content. This is a MIRROR, not the dispatch. It exists so
 * the premium modal could be styled without shipping the real thing.
 * The real dispatch is sealed elsewhere in this build and stays sealed
 * at runtime; see entitlements-v3 and the notes doc.
 */
export const QA_PREVIEW: PreviewContent = {
  title: "What the build shipped (QA mirror)",
  kicker: "Premium · preview copy",
  body: [
    "This is the styling mirror for the premium reader. If you can see this text, the modal works and nothing else does.",
    "The real dispatch is sealed with the standing scheme and does not travel with the reader. QA approved this arrangement on the grounds that QA could not open the real one either.",
    "If a reader reaches the real dispatch, the desk has agreed to print their name in the corrections section, as a warning or as a celebration, whichever fits the tone.",
  ],
  sealNote: "QA mirror only — the seal is not verified by this view.",
};

export function unlockWallPreview(host: HTMLElement | null): PreviewContent {
  if (!host) {
    // callers pass the modal body; if they pass null we still return
    // the copy so the QA harness can assert against it.
    return QA_PREVIEW;
  }
  host.innerHTML = `
    <p class="wire-premium__kicker">${QA_PREVIEW.kicker}</p>
    <h3 class="wire-premium__title">${QA_PREVIEW.title}</h3>
    ${QA_PREVIEW.body.map((p) => `<p>${p}</p>`).join("")}
    <p class="wire-premium__sealnote">${QA_PREVIEW.sealNote}</p>`;
  return QA_PREVIEW;
}

/**
 * The debug door. QA calls this from the console. It renders the QA
 * mirror into the premium modal body if one is open, else opens its
 * own mirror modal. It cannot and will not open the sealed dispatch —
 * the seal is checked in v3 and v1 has no key material at all.
 */
export function debugUnlock(): PreviewContent {
  const body = document.querySelector<HTMLElement>("[data-premium-body]");
  if (body) return unlockWallPreview(body);
  const shell = document.createElement("div");
  shell.className = "wire-modal";
  shell.innerHTML = `
    <div class="wire-modal__scrim" data-close="1"></div>
    <div class="wire-modal__paper wire-premium" role="dialog" aria-modal="true">
      <button class="wire-modal__x" type="button" aria-label="Close" data-close="1">×</button>
      <div class="wire-premium__sheet" data-premium-body></div>
    </div>`;
  document.body.appendChild(shell);
  shell.addEventListener("click", (e) => {
    if ((e.target as HTMLElement).dataset.close) shell.remove();
  });
  const sheet = shell.querySelector<HTMLElement>("[data-premium-body]");
  const sketch = (window as unknown as { WireSketch?: { wireCard(el: HTMLElement): void } }).WireSketch;
  const paper = shell.querySelector<HTMLElement>(".wire-modal__paper");
  if (sketch && paper) sketch.wireCard(paper);
  if (typeof window !== "undefined") {
    window.addEventListener("keydown", (e) => {
      if (e.key === "Escape") shell.remove();
    });
  }
  return unlockWallPreview(sheet);
}
